import { Fragment, useEffect, useRef, useState } from 'react';
import { useAuth } from './lib/auth.jsx';
import Today from './pages/Today.jsx';
import Console from './pages/Console.jsx';
import Classifications from './pages/Classifications.jsx';
import Dossier from './pages/Dossier.jsx';
import PermitSheet from './pages/PermitSheet.jsx';
import Screening from './pages/Screening.jsx';
import Admin from './pages/Admin.jsx';
import Brands from './pages/Brands.jsx';
import Handbook from './pages/Handbook.jsx';
import MaterialReview from './pages/MaterialReview.jsx';
import { MIN_PW, NO_BRAND, NO_CAT, getClassification } from './lib/data.js';
import { readRoute, routeUrl, writeUrl, hasAppHistory } from './lib/route.js';
import { leaveBlocker } from './lib/leaveguard.js';

// ย่อ/ขยายตัวหนังสือทั้งแอพ (zoom ทั้ง .wrap → สัดส่วนไม่เพี้ยน) + จำค่าไว้ในเครื่อง
const ZOOM_KEY = 'ib_app_zoom';
const ZMIN = 0.8, ZMAX = 1.6, ZSTEP = 0.1;
const clampZoom = (v) => Math.round(Math.min(ZMAX, Math.max(ZMIN, v)) * 100) / 100;

function FontControl() {
  const [zoom, setZoom] = useState(() => {
    try { const v = parseFloat(localStorage.getItem(ZOOM_KEY)); return Number.isFinite(v) ? clampZoom(v) : 1; }
    catch { return 1; }
  });
  useEffect(() => {
    document.documentElement.style.setProperty('--app-zoom', String(zoom));
    try { localStorage.setItem(ZOOM_KEY, String(zoom)); } catch { /* โหมดส่วนตัว */ }
  }, [zoom]);
  return (
    <div className="fontctl" title="ย่อ/ขยายขนาดตัวหนังสือทั้งแอพ">
      <button className="fz" onClick={() => setZoom(z => clampZoom(z - ZSTEP))}
        disabled={zoom <= ZMIN} aria-label="ตัวหนังสือเล็กลง">A−</button>
      <button className="fzval" onClick={() => setZoom(1)} title="กลับขนาดปกติ">{Math.round(zoom * 100)}%</button>
      <button className="fz big" onClick={() => setZoom(z => clampZoom(z + ZSTEP))}
        disabled={zoom >= ZMAX} aria-label="ตัวหนังสือใหญ่ขึ้น">A+</button>
    </div>
  );
}

const TABS = [
  { id: 'today', label: 'วันนี้', adminOnly: false },
  { id: 'console', label: 'Console', adminOnly: false },
  { id: 'brands', label: 'แบรนด์', adminOnly: false, costOnly: true },   // ข้อมูลคู่ค้า (เงื่อนไข/ราคา/บันทึก) = ทีมที่ดูต้นทุนได้
  { id: 'products', label: 'สินค้า & HS', adminOnly: false },
  { id: 'materials', label: 'วัสดุจาก AI', adminOnly: false, aiOnly: true },   // แอดมิน + ผู้ประสานงาน เท่านั้น (ไม่ใช่ can_see_cost — นั่นรวมบัญชีด้วย)
  { id: 'screening', label: 'คัดกรองก่อนสั่ง', adminOnly: false },
  { id: 'handbook', label: 'วิธีทำงาน', adminOnly: false },
  { id: 'analytics', label: 'Analytics', adminOnly: true },
  { id: 'settings', label: 'Settings', adminOnly: true },
];

function Login() {
  const { signIn, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null); setNote(null);
    const { error } = await signIn(email, password);
    if (error) setErr('เข้าไม่ได้ — เช็คอีเมล/รหัสผ่านอีกครั้ง');
    setBusy(false);
  };

  const forgot = async () => {
    setErr(null); setNote(null);
    if (!email.trim()) { setErr('พิมพ์อีเมลของคุณในช่องด้านบนก่อน แล้วกด "ลืมรหัสผ่าน" อีกครั้ง'); return; }
    setBusy(true);
    const { error } = await resetPassword(email.trim());
    setBusy(false);
    if (error) setErr('ส่งลิงก์ไม่สำเร็จ — เช็คอีเมลอีกครั้ง');
    else setNote(`ส่งลิงก์ตั้งรหัสผ่านใหม่ไปที่ ${email.trim()} แล้ว — เปิดเมลแล้วกดลิงก์เพื่อตั้งรหัสใหม่`);
  };

  return (
    <div className="login">
      <div className="cap"/>
      <form className="bd" onSubmit={submit}>
        <h1>INBOUND<span style={{ fontWeight: 400 }}>point</span></h1>
        <p>เข้าสู่ระบบด้วยบัญชีพนักงาน</p>
        <label>อีเมล</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" required/>
        <label>รหัสผ่าน</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required/>
        <button className="btn" disabled={busy}>{busy ? 'กำลังเข้า…' : 'เข้าสู่ระบบ'}</button>
        <button type="button" className="linkbtn" disabled={busy} onClick={forgot}>ลืมรหัสผ่าน?</button>
        {err && <div className="err">{err}</div>}
        {note && <div className="oknote">{note}</div>}
        <a className="guidelink" href="/guide" target="_blank" rel="noopener">คู่มือการใช้งาน (สำหรับพนักงาน) →</a>
      </form>
    </div>
  );
}

// ใช้ 3 ทาง: ลิงก์ "ลืมรหัสผ่าน" · แอดมินเพิ่งตั้งรหัสให้ (บังคับเปลี่ยน) · กดเองจากเมนู
function SetNewPassword({ forced, onDone, onCancel }) {
  const { updatePassword } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    // ใช้เพดานเดียวกับฝั่งเซิร์ฟเวอร์ (db.MIN_PW) — เดิมที่นี่ยอม 4 ตัว ทำให้รหัสที่แอดมินตั้งให้
    // 9 ตัว ถูกลดเหลือ 4 ตัวได้ บนบัญชีที่เปิดได้ทุกแอพของบริษัท
    if (pw.length < MIN_PW) { setErr(`รหัสผ่านอย่างน้อย ${MIN_PW} ตัวอักษร`); return; }
    if (pw !== pw2) { setErr('รหัสผ่านสองช่องไม่ตรงกัน'); return; }
    setBusy(true);
    const { error } = await updatePassword(pw);
    setBusy(false);
    if (error) {
      setErr(/weak|short|at least|length/i.test(error.message ?? '')
        ? `รหัสผ่านอย่างน้อย ${MIN_PW} ตัวอักษร`
        : 'ตั้งรหัสไม่สำเร็จ — ' + (error.message ?? 'ลองใหม่อีกครั้ง'));
      return;
    }
    onDone?.();
  };

  return (
    <div className="login">
      <div className="cap"/>
      <form className="bd" onSubmit={submit}>
        <h1>INBOUND<span style={{ fontWeight: 400 }}>point</span></h1>
        <p>{forced
          ? 'แอดมินเพิ่งตั้งรหัสผ่านให้ — ขอให้ช่วยตั้งรหัสของตัวเองก่อนเริ่มใช้งาน'
          : 'ตั้งรหัสผ่านใหม่'}</p>
        <label>รหัสผ่านใหม่</label>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" required/>
        <label>พิมพ์รหัสผ่านใหม่อีกครั้ง</label>
        <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" required/>
        <button className="btn" disabled={busy}>{busy ? 'กำลังบันทึก…' : 'บันทึกรหัสใหม่ แล้วเข้าใช้งาน'}</button>
        {onCancel && <button type="button" className="tab" style={{ marginTop: 10 }} onClick={onCancel}>ยังไม่เปลี่ยน กลับหน้าเดิม</button>}
        {err && <div className="err">{err}</div>}
      </form>
    </div>
  );
}

// หน้าเอกสารใบอนุญาตที่เปิดจากลิงก์ /permit/<id> ตรงๆ (รีเฟรช / แท็บใหม่) — ยังไม่มีข้อมูลสินค้าในมือ ต้องดึงใบนั้นก่อน
function PermitLoader({ id, mock, canCost, onBack }) {
  const [st, setSt] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    setSt({ loading: true });
    (mock ? Promise.resolve({ mock: true }) : getClassification(id))
      .then(cls => { if (alive) setSt({ cls }); })
      // 22P02 = id ในลิงก์ไม่ใช่รูป uuid (ก๊อปมาไม่ครบ) → "ไม่พบ" ไม่โชว์ข้อความ Postgres ดิบให้พนักงาน
      .catch(e => { if (alive) setSt(e?.code === '22P02' ? { cls: null } : { error: e.message ?? String(e) }); });
    return () => { alive = false; };
  }, [id, mock]);
  if (st.loading) return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;
  if (st.cls?.mock) return (
    <div className="placeholder">
      <h2>โหมด MOCK</h2><p>หน้านี้เปิดได้จากหน้าสินค้าเท่านั้น (ไม่มีข้อมูลจริงให้ดึงตาม id)</p>
      <button className="tab" onClick={onBack}>กลับหน้าสินค้า</button>
    </div>
  );
  if (!st.cls) return (
    <div className="placeholder">
      <h2>{st.error ? 'เปิดหน้านี้ไม่ได้' : 'ไม่พบสินค้านี้'}</h2>
      <p>{st.error ? `โหลดข้อมูลสินค้าไม่สำเร็จ — ${st.error}` : 'สินค้าอาจถูกลบไปแล้ว หรือลิงก์ไม่ครบ'}</p>
      <button className="tab" onClick={onBack}>กลับหน้าสินค้า</button>
    </div>
  );
  return <PermitSheet cls={st.cls} canCost={canCost} onBack={onBack}/>;
}

// ค่า "ไม่ระบุ" ของแบรนด์/หมวดในหน้าสินค้า → แปลงเป็น _none ใน URL (lib/route.js)
const RT = { noBrand: NO_BRAND, noCat: NO_CAT };
const viewOf = (r) => ({ q: r.q ?? '', brand: r.brand ?? null, cat: r.cat ?? null });
const TAB_LABEL = Object.fromEntries(TABS.map(t => [t.id, t.label]));
// ไม่ชวนขอสิทธิ์ดูต้นทุน — กฎบริษัท: คลังห้ามเห็นต้นทุน (หน้าแบรนด์/ใบอนุญาตมีข้อมูลราคา)
//   moved = พามาหน้า "วันนี้" แล้ว (เปิดจากลิงก์) · false = กดปุ่มในแอพ อยู่หน้าเดิม
const noRight = (r, moved = true) => {
  const t = TABS.find(x => x.id === r.tab);
  const name = r.permit ? 'เอกสารยื่นขอใบอนุญาต' : TAB_LABEL[r.tab] ?? r.tab;
  const tail = moved ? ' — พามาหน้า "วันนี้" แทน' : '';
  if (r.permit || t?.costOnly) return `หน้า "${name}" มีข้อมูลราคา เปิดได้เฉพาะทีมที่ดูต้นทุนได้${tail}`;
  if (t?.adminOnly) return `หน้า "${name}" สำหรับแอดมินเท่านั้น${tail}`;
  if (t?.aiOnly) return `หน้า "${name}" เปิดให้เฉพาะแอดมินและผู้ประสานงาน${tail}`;
  return `บัญชีนี้เปิดหน้า "${name}" ไม่ได้${tail}`;
};
// รหัสจาก URL ที่จะพิมพ์ลงแถบแจ้ง — ใครก็ส่งลิงก์ได้ → แสดงเฉพาะที่หน้าตาเป็นรหัสจริง (กันข้อความแปลก ๆ ในแถบของแอพ)
const codeOrNull = (v) => (/^[\w .\-\/]{1,40}$/.test(String(v ?? '')) ? String(v) : null);
// ชื่อแท็บเบราว์เซอร์ตามหน้า — ประวัติ/บุ๊กมาร์ก/หลายแท็บ แยกออกว่าอันไหนหน้าไหน
const titleOf = (r) => {
  const head = r.dossier ? 'ใบข้อมูลสินค้า' : r.permit ? 'เอกสารยื่นขอใบอนุญาต' : TAB_LABEL[r.tab] ?? 'วันนี้';
  const q = String(r.q ?? '').replace(/[\u0000-\u001f\u007f]/g, '').trim();   // คำค้นไทยใช้ได้ แต่ตัดอักขระควบคุม + จำกัดความยาว
  const sub = r.tab === 'console' && !r.dossier ? codeOrNull(r.ship) : r.tab === 'screening' ? codeOrNull(r.hs)
    : r.tab === 'products' && !r.permit && q ? (q.length > 40 ? `${q.slice(0, 40)}…` : q) : null;
  return `${head}${sub ? ` · ${sub}` : ''} — INBOUNDpoint`;
};

export default function App() {
  const { session, me, signOut, mock, recovery } = useAuth();
  // หน้าที่เปิดอยู่มาจาก URL (lib/route.js) — รีเฟรช/เปิดแท็บใหม่แล้วอยู่หน้าเดิม · ไม่มี = "วันนี้"
  const [boot] = useState(() => readRoute(RT));
  const [route, setRoute] = useState(boot);
  const routeRef = useRef(boot);                      // ค่าล่าสุดสำหรับ keep() ที่หน้าเรียกจาก effect
  const [nav, setNav] = useState(0);                  // นับการ "ไปหน้าใหม่" → key ของหน้า = เริ่มใหม่ อ่านค่าจาก URL
  const [permitCls, setPermitCls] = useState(null);   // สินค้าที่ส่งมาจากหน้าสินค้า (มีรูปที่ยังไม่บันทึก) — เปิดจากลิงก์ = PermitLoader ดึงเอง
  const [stats, setStats] = useState(null);           // สรุปตัวเลข header จาก Console (ตามข้อมูลจริง)
  // "เปิดตรงนี้" ที่ส่งให้หน้า — ใช้ครั้งเดียวแล้วหน้าล้างทิ้ง (onFocusConsumed) ไม่งั้นโหลดใหม่แล้วเด้งกลับค่าเดิม
  const [focusShip, setFocusShip] = useState(boot.ship ?? null);     // รหัสตู้ใน Console
  const [focusBrand, setFocusBrand] = useState(boot.partner ?? null); // id แบรนด์ในแท็บ "แบรนด์"
  const [focusView, setFocusView] = useState(boot.tab === 'products' && !boot.permit ? viewOf(boot) : null); // แบรนด์/หมวด/คำค้นในแท็บ "สินค้า & HS"
  const [focusHs, setFocusHs] = useState(boot.hs ?? null);           // พิกัดที่เปิดดูในแท็บ "คัดกรองก่อนสั่ง"
  const [focusZone, setFocusZone] = useState(boot.zone ?? null);     // หน้าย่อยของ "ตั้งค่า"
  const [focusMaterial, setFocusMaterial] = useState(boot.focus ?? null); // แถวในหน้า "วัสดุจาก AI"
  const [pwSelf, setPwSelf] = useState(false);        // กดเปลี่ยนรหัสผ่านของตัวเองจากเมนู
  const [notice, setNotice] = useState(null);         // แจ้งว่าทำไมไม่ได้ไปหน้าที่ลิงก์ชี้ (ไม่มีสิทธิ์ / ของถูกลบ)
  const bypassGuard = useRef(false);                  // คนตอบ "ออกเลย" ในกล่องถามแล้ว → popstate ถัดไปไม่ต้องถามซ้ำ

  // สิทธิ์ต่อหน้า — URL พิมพ์เองได้ ทุกทางที่พาไปหน้าไหนต้องผ่าน canOpen
  const isAdmin = !!me?.is_admin;
  const canCost = mock || !!me?.can_see_cost;
  const canAi = mock || ['admin', 'coordinator'].includes(me?.role);   // หาวัสดุด้วย AI — ตรงกับด่านฝั่งเซิร์ฟเวอร์
  const tabs = TABS.filter(t => (!t.adminOnly || isAdmin) && (!t.costOnly || canCost) && (!t.aiOnly || canAi));
  const meReady = mock || !!me;                       // สิทธิ์ยังโหลดไม่เสร็จ = รอก่อน (แอดมินรีเฟรช Analytics ต้องไม่เด้งออก)
  // แอดมินเพิ่งตั้งรหัสให้ → บังคับให้เจ้าตัวตั้งของตัวเองก่อน (deploy-35)
  // รหัสที่แอดมินพิมพ์และส่งต่อทางแชท จึงไม่กลายเป็นรหัสถาวรของบัญชีที่เปิดได้ทุกแอพ
  const mustChangePw = !mock && session?.user?.user_metadata?.must_change_pw === true;
  const sessionReady = session !== undefined;
  // หน้าในแอพโชว์อยู่ไหม (ไม่ใช่หน้า login / ตั้งรหัส / ยังไม่เปิดสิทธิ์)
  const shellShown = sessionReady && (mock || (!!session && !recovery && !mustChangePw && me?.role !== 'none'));
  const uid = mock ? 'mock' : session?.user?.id ?? null;
  const canOpen = (r) => (r.dossier ? true : r.permit ? canCost : tabs.some(t => t.id === r.tab));
  const allowed = canOpen(route);

  // ไปหน้าใหม่ (กดแท็บ / เปิดตู้ / ปุ่มย้อนกลับของเบราว์เซอร์) → หน้านั้นเริ่มใหม่ตามค่าใน route
  // ค่า "เปิดตรงนี้" ของทุกหน้าจาก route (หน้าที่ mount ครั้งถัดไปอ่านค่านี้)
  const seedFocus = (r) => {
    setFocusShip(r.ship ?? null);
    setFocusBrand(r.partner ?? null);
    setFocusView(r.tab === 'products' && !r.permit ? viewOf(r) : null);
    setFocusHs(r.hs ?? null);
    setFocusZone(r.zone ?? null);
    setFocusMaterial(r.focus ?? null);
  };
  const enter = (r) => {
    routeRef.current = r;
    setRoute(r);
    seedFocus(r);
    setPermitCls(null);
    setPwSelf(false);
    setNotice(null);
    setNav(n => n + 1);
  };
  const go = (r) => {
    // หน้าที่เปิดไม่ได้ → บอกเหตุผล ไม่เพิ่มประวัติ (เดิมเพิ่มแล้วโดนดีดกลับ = กดย้อนกลับครั้งถัดไปไม่ไปไหน)
    if (meReady && !canOpen(r)) { setNotice(noRight(r, false)); return; }
    writeUrl(routeUrl(r, RT), { push: true });
    enter(r);
  };
  // ปุ่ม "← กลับ" ในหน้า = เหมือนปุ่มย้อนกลับของเบราว์เซอร์ (กลับไปตู้/ตัวกรองเดิม)
  //   เปิดจากลิงก์ตรงๆ (ไม่มีหน้าก่อนในแอพ) → แทนที่ด้วย fallback ไม่ซ้อนประวัติ (ย้อนกลับแล้วไม่วนกลับมาหน้านี้)
  const back = (fallback) => {
    if (hasAppHistory()) { window.history.back(); return; }
    writeUrl(routeUrl(fallback, RT));
    enter(fallback);
  };
  // เลือกของในหน้าเดิม (ตู้/แบรนด์/คำค้น) → อัปเดต URL อย่างเดียว ไม่เพิ่มประวัติ ไม่เริ่มหน้าใหม่
  //   tab = หน้าที่เรียก — ถ้าเปลี่ยนหน้าไปแล้ว (effect ที่มาช้า) ห้ามเขียนทับ URL ของหน้าใหม่
  const keep = (tab, patch) => {
    const cur = routeRef.current;
    if (cur.tab !== tab || cur.dossier || cur.permit) return;
    const n = { ...cur, ...patch };
    routeRef.current = n;
    writeUrl(routeUrl(n, RT));
    setRoute(n);
  };
  // ปิดหน้า "รหัสผ่านของฉัน" → หน้าข้างใต้เริ่มใหม่ตาม URL ล่าสุด (ตู้/ตัวกรองที่เลือกไว้ล่าสุด ไม่ใช่ค่าตอนเข้าหน้า)
  const closePw = () => enter(routeRef.current);

  // ปุ่มย้อนกลับ/ไปข้างหน้าของเบราว์เซอร์ · เปลี่ยนแค่ #… (เช่น Supabase ล้าง token) = ไม่ใช่การเปลี่ยนหน้า
  //   ยังมีฟอร์มที่ไม่ได้บันทึก (lib/leaveguard.js) → ดึง URL กลับหน้าเดิมก่อน แล้วถาม · ตอบ "ออกเลย" = ย้อนให้อีกที
  useEffect(() => {
    const onPop = () => {
      const skip = bypassGuard.current;
      bypassGuard.current = false;
      const r = readRoute(RT);
      // ย้อนกลับไปเจอรายการที่ยังมี #… (เช่น token ของลิงก์ลืมรหัสผ่านที่ Supabase ล้างด้วยการเพิ่มรายการใหม่) → ล้างทิ้งทันที
      if (window.location.hash) writeUrl(routeUrl(r, RT));
      if (routeUrl(r, RT) === routeUrl(routeRef.current, RT)) return;
      const msg = skip ? null : leaveBlocker();
      if (msg) {
        writeUrl(routeUrl(routeRef.current, RT), { push: true });
        if (window.confirm(msg)) { bypassGuard.current = true; window.history.back(); }
        return;
      }
      enter(r);
    };
    // รีเฟรช / ปิดแท็บ ตอนมีงานค้าง → เบราว์เซอร์ถามก่อน (ข้อความของเบราว์เซอร์เอง)
    const onUnload = (e) => { if (leaveBlocker()) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('popstate', onPop);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  // URL ที่ไม่ตรงรูป (/foo · /dossier เปล่า · #… ที่ค้าง) → เขียนให้ตรงหน้าที่โชว์จริง
  //   ต้องรอ session โหลดเสร็จก่อน = Supabase อ่าน token ใน #… ของลิงก์ "ลืมรหัสผ่าน" เสร็จแล้ว
  useEffect(() => { if (sessionReady) writeUrl(routeUrl(routeRef.current, RT)); }, [sessionReady]);

  // หน้าในแอพถูกถอด (ออกจากระบบ / หลุด session / บังคับตั้งรหัส) แล้วกลับมา → เปิดตาม URL ล่าสุด
  //   (ค่า "เปิดตรงนี้" ถูกใช้ไปแล้วตอนเข้าครั้งแรก ไม่งั้นได้ตู้แรก/ไม่มีตัวกรอง แล้ว URL โดนเขียนทับ — รีวิว 28 ต.ค. 69)
  //   คนละคนกับเจ้าของหน้าเดิม (เครื่องใช้ร่วมกัน) → เริ่มที่ "วันนี้" ไม่รับหน้าของคนก่อน
  const ownerRef = useRef(null);
  useEffect(() => {
    if (!shellShown) { seedFocus(routeRef.current); return; }
    if (ownerRef.current && uid && ownerRef.current !== uid) { writeUrl('/'); enter({ tab: 'today' }); }
    if (uid) ownerRef.current = uid;
  }, [shellShown, uid]);

  useEffect(() => { document.title = titleOf(route); }, [route]);

  // แถบแจ้งหายเองเมื่อคนเริ่มทำอย่างอื่น (คลิกที่ไหนก็ได้นอกแถบ) — ไม่ค้างจนดูเหมือนพูดถึงตู้/แบรนด์ที่เพิ่งกด
  //   ฟัง click (จบท่ากดแล้ว) ไม่ใช่ pointerdown — แถบหายตอนกดลง = หน้าเลื่อนขึ้นใต้นิ้ว คลิกไปโดนปุ่มอื่น
  useEffect(() => {
    if (!notice) return;
    // คลิกที่ทำให้แถบขึ้น (เช่นปุ่ม "เปิดแบรนด์") ยังเดินทางไม่ถึง document ตอนตัวฟังนี้ถูกติด → ข้ามคลิกที่เกิดก่อนแถบขึ้น
    const shownAt = performance.now();
    const off = (e) => { if (e.timeStamp > shownAt && !e.target?.closest?.('.navnote')) setNotice(null); };
    document.addEventListener('click', off);
    return () => document.removeEventListener('click', off);
  }, [notice]);

  // เปิดตู้เจาะจงใน Console (คลิกงานในแท็บ "วันนี้" / ตู้ในแท็บ "แบรนด์")
  const openShipment = (code) => go({ tab: 'console', ship: code });
  // เปิดแบรนด์เจาะจงในแท็บ "แบรนด์"
  const openBrand = (id) => go({ tab: 'brands', partner: id });
  // เปิดสินค้าเจาะจงในแท็บ "สินค้า & HS" (มาจากแผง "ยังไม่ครบ" ในใบข้อมูลสินค้า)
  const openProduct = (sku) => go({ tab: 'products', q: sku });
  const openDossier = (id) => go({ tab: 'console', dossier: id });
  const openPermit = (cls) => { go({ tab: 'products', permit: cls.id }); setPermitCls(cls); };
  // id = แถวที่ให้เปิดเลย (ไม่ส่ง = เปิดหน้าเฉย ๆ) · ปุ่มบางที่ส่ง event มา จึงรับเฉพาะข้อความ
  const openMaterials = (id) => go({ tab: 'materials', ...(typeof id === 'string' && id ? { focus: id } : {}) });

  // หน้าใน URL ที่ไม่ได้เปิดให้คนนี้ (Analytics/Settings/แบรนด์/ใบอนุญาต) → กลับ "วันนี้" พร้อมบอกเหตุผล
  //   role 'none' (ยังไม่เปิดสิทธิ์ / โหลดสิทธิ์ไม่ขึ้น) = ขึ้นหน้าแจ้งอยู่แล้ว → ไม่แตะ URL (โหลดได้รอบหน้าจะได้กลับหน้าเดิม)
  useEffect(() => {
    if (!meReady || allowed || me?.role === 'none') return;
    const was = routeRef.current;
    writeUrl('/');
    enter({ tab: 'today' });
    setNotice(noRight(was));
  }, [meReady, allowed]);

  if (session === undefined) return null;                 // loading session
  if (!mock && !session) return <div className="wrap"><Login/></div>;
  if (!mock && (recovery || mustChangePw)) return (
    <div className="wrap"><SetNewPassword forced={mustChangePw && !recovery}/></div>
  );
  if (!mock && me && me.role === 'none') return (
    <div className="wrap">
      <div className="placeholder">
        {me.dbPending ? (
          <>
            <h2>ฐานข้อมูลยังไม่พร้อม</h2>
            <p>ระบบหลังบ้าน (Supabase) กำลังมีเหตุขัดข้อง — schema ของแอพรอเปิดใช้อยู่
              จะกลับมาใช้ได้เองโดยไม่ต้องทำอะไร ลองกดเข้าใหม่อีกครั้งภายหลัง</p>
            <button className="tab" onClick={() => window.location.reload()}>ลองใหม่</button>
          </>
        ) : (
          <>
            <h2>ยังไม่ได้เปิดสิทธิ์ใช้งาน</h2>
            <p>บัญชีนี้ล็อกอินถูกต้องแล้ว แต่ยังไม่ได้เปิดให้ใช้ INBOUNDpoint —
              ฝากแจ้งแอดมินให้เปิดสิทธิ์ให้ที่ <b>ตั้งค่า › ผู้ใช้ &amp; สิทธิ์</b> (ใช้เวลาไม่กี่วินาที)</p>
          </>
        )}
        <button className="tab" onClick={signOut}>ออกจากระบบ</button>
      </div>
    </div>
  );

  const tab = route.tab;
  return (
    <div className="wrap">
      <div className="top">
        <div className="brand">
          <div className="logo">INBOUND<span className="pt">point</span></div>
          <div className="tag">Import Coordinator Console</div>
        </div>
        <div className="sum">
          {mock && <span className="mockbar">MOCK — ยังไม่ต่อ Supabase</span>}
          {stats && <>
            <span className="schip">ACTIVE <b>{stats.active}</b></span>
            {stats.urgent > 0 && <span className="schip hot">ต้องเร่ง <b>{stats.urgent}</b></span>}
            {stats.watch > 0 && <span className="schip warm">ถึงใน 7 วัน <b>{stats.watch}</b></span>}
          </>}
        </div>
      </div>

      <div className="tabs">
        {tabs.map(t => (
          <button key={t.id} className={`tab ${tab === t.id && !route.dossier ? 'on' : ''}`}
            onClick={() => {
              // กดแท็บที่เปิดอยู่แล้ว = อยู่ที่เดิม (ไม่ล้างตัวกรอง/ตู้ที่เลือก) แค่ปิดหน้าตั้งรหัสถ้าเปิดอยู่
              if (tab === t.id && !route.dossier && !route.permit) { if (pwSelf) closePw(); return; }
              go({ tab: t.id });
            }}>
            {t.label}
          </button>
        ))}
        <FontControl/>
        {!mock && <a className="tab" href="/guide" target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>คู่มือ</a>}
        {!mock && <button className="tab" onClick={() => setPwSelf(true)}>รหัสผ่านของฉัน</button>}
        {!mock && <button className="tab" onClick={signOut}>ออก</button>}
      </div>

      {notice && (
        <div className="navnote noprint">
          <span>{notice}</span>
          <button className="navnote-x" onClick={() => setNotice(null)} title="ปิด">✕</button>
        </div>
      )}

      {pwSelf
        ? <SetNewPassword onDone={closePw} onCancel={closePw}/>
        : !allowed
        ? (meReady ? null : <div className="placeholder"><h2>กำลังโหลด…</h2></div>)   // สิทธิ์ยังไม่มา — ไม่ปล่อยจอว่าง
        : route.permit
        ? (permitCls?.id === route.permit
            ? <PermitSheet key={nav} cls={permitCls} canCost={canCost} onBack={() => back({ tab: 'products' })}/>
            : <PermitLoader key={nav} id={route.permit} mock={mock} canCost={canCost} onBack={() => back({ tab: 'products' })}/>)
        : route.dossier
        ? <Dossier key={nav} consignmentId={route.dossier} onOpenProduct={openProduct} onOpenPermit={(cls) => go({ tab: 'products', permit: cls.id })} canAi={canAi} onOpenMaterials={openMaterials}
            onBack={(code) => back(code ? { tab: 'console', ship: code } : { tab: 'console' })}/>
        : <Fragment key={nav}>
            {tab === 'today' && <Today onOpenShipment={openShipment} onOpenBrand={openBrand} onOpenDossier={openDossier}/>}
            {tab === 'console' && <Console onOpenDossier={openDossier} onStats={setStats} onOpenMaterials={openMaterials}
              focusShip={focusShip} onSelect={(id) => keep('console', { ship: id })}
              onFocusConsumed={(found) => {
                if (found === false) setNotice(`ไม่พบตู้${codeOrNull(focusShip) ? ` ${focusShip}` : 'ที่ลิงก์ชี้ไว้'} — อาจถูกยกเลิกหรือเปลี่ยนรหัสแล้ว ลองค้นที่ช่องค้นหาตู้ด้านซ้าย · ตอนนี้เปิดตู้แรกของรายการให้แทน`);
                setFocusShip(null);
              }}/>}
            {tab === 'brands' && <Brands onOpenShipment={openShipment}
              focusBrand={focusBrand} onSelect={(id) => keep('brands', { partner: id ?? undefined })}
              onFocusConsumed={(found) => {
                if (found === false) setNotice('ไม่พบแบรนด์ที่ลิงก์ชี้ไว้ (อาจถูกลบไปแล้ว)');
                setFocusBrand(null);
              }}/>}
            {tab === 'products' && <Classifications onOpenPermit={openPermit} canCost={canCost} canAi={canAi} onOpenMaterials={openMaterials}
              focus={focusView} onFocusConsumed={() => setFocusView(null)}
              onView={(v) => keep('products', v)}/>}
            {tab === 'screening' && <Screening focusHs={focusHs} onPick={(hs) => keep('screening', { hs })}
              onMissing={(hs) => setNotice(`ไม่พบพิกัด${codeOrNull(hs) ? ` ${hs}` : 'ที่ลิงก์ชี้ไว้'} ในกฎพิกัด — อาจถูกลบหรือแก้รหัสแล้ว`)}/>}
            {tab === 'materials' && <MaterialReview onOpenProducts={() => go({ tab: 'products' })}
              focusId={focusMaterial}
              onFocusConsumed={(found) => {
                if (found === false) { setNotice('ไม่พบงานหาวัสดุที่ลิงก์ชี้ไว้ — อาจถูกลบไปแล้ว'); keep('materials', { focus: undefined }); }
                setFocusMaterial(null);
              }}/>}
            {tab === 'handbook' && <Handbook/>}
            {tab === 'analytics' && <Admin view="analytics"/>}
            {tab === 'settings' && <Admin view="settings" zone={focusZone} onZone={(z) => keep('settings', { zone: z })}/>}
          </Fragment>}
    </div>
  );
}
